import { Plus, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { BatchGuideButton } from "../components/guide/BatchGuideButton";
import { BatchesSection, InternalBatchesSection } from "../components/list/BatchesSection";
import { usePayoutBatchesList } from "../hooks/usePayoutBatchesList";
import { newBatchPath, PAYOUT_BATCHES_PATH } from "../utils/batchLinks";
import { canWriteBatches } from "../utils/batchPermissions";

/**
 * `/app/payout-batches` — every batch the person can see, newest first, with the KPI strip as the status
 * filter. Access is the backend's call (`payout_batches:read`): a 401/403 latches into the no-access view.
 */
const PayoutBatchesListPage = () => {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const { isInternalUser, user } = useAuth();
  const list = usePayoutBatchesList();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payout_batches"), to: PAYOUT_BATCHES_PATH },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  if (!list.hasAccess) return <NoAccessView />;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <div className="flex items-center gap-2">
            {list.lastUpdatedAt && (
              <span
                className="hidden font-mono text-xs text-muted-foreground tabular-nums sm:inline"
                aria-live="polite"
              >
                {t("list_live_updated_at", {
                  time: list.lastUpdatedAt.toLocaleTimeString(activeLanguage, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                })}
              </span>
            )}
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn(list.isRefreshing && "[&_svg]:animate-spin")}
              onClick={() => void list.refresh()}
            />
            <BatchGuideButton />
            {canWriteBatches(user) && (
              <ColocaButton variant="primary" icon={Plus} onClick={() => navigate(newBatchPath())}>
                {t("action_new_batch")}
              </ColocaButton>
            )}
          </div>
        }
      />

      {isInternalUser ? (
        <InternalBatchesSection list={list} />
      ) : (
        <BatchesSection list={list} catalog={null} />
      )}
    </div>
  );
};

export default PayoutBatchesListPage;
